import React, { useState, useEffect } from 'react';
import { Settings, CheckCircle, XCircle, Minus, Info } from 'react-feather';
import { Popover } from 'antd';
import axios from 'axios';

const MachineStatistics = () => {
    const [statistics, setStatistics] = useState({
        totalMachines: 0,
        activeMachines: 0,
        inactiveMachines: 0,
        totalSparepartExpenses: 0,
        loading: true
    });

    const [machines, setMachines] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });
    const [selectedMachine, setSelectedMachine] = useState('all');

    useEffect(() => {
        fetchMachineStatistics();
    }, [selectedMonth, selectedMachine]);

    const fetchMachineStatistics = async () => {
        try {
            setStatistics(prev => ({ ...prev, loading: true }));
            
            const params = {};
            
            // Add date filters
            if (selectedMonth !== 'all') {
                params.from_date = `${selectedMonth}-01`;
                params.to_date = `${selectedMonth}-31`;
            }
            
            // Add machine filter
            if (selectedMachine !== 'all') {
                params.machine_id = selectedMachine;
            }

            const response = await axios.get('/dashboard/machine-statistics', { params });
            const { data, machines: machineOptions } = response.data;

            // Set machines for dropdown (only on first load)
            if (machines.length === 0) {
                setMachines(machineOptions || []);
            }

            setStatistics({
                totalMachines: data.total_machines,
                activeMachines: data.active_machines,
                inactiveMachines: data.inactive_machines,
                totalSparepartExpenses: data.total_sparepart_expenses,
                loading: false
            });
        } catch (error) {
            console.error('Error fetching machine statistics:', error);
            setStatistics(prev => ({ ...prev, loading: false }));
        }
    };

    const formatNumber = (num) => {
        const absNum = Math.abs(num);
        if (absNum >= 1000000) {
            return (num / 1000000).toFixed(1) + ' mln';
        } else if (absNum >= 1000) {
            return (num / 1000).toFixed(1) + ' ming';
        }
        return num.toString();
    };

    const cards = [
        {
            title: 'Jami mashinalar',
            value: statistics.totalMachines,
            icon: Settings,
            color: 'info',
            suffix: 'ta',
            info: "Tizimdagi barcha mashinalar soni"
        },
        {
            title: 'Faol mashinalar',
            value: statistics.activeMachines,
            icon: CheckCircle,
            color: 'success',
            suffix: 'ta',
            info: "Holati 'active' bo'lgan mashinalar"
        },
        {
            title: 'Ehtiyot qism xarajatlari',
            value: formatNumber(statistics.totalSparepartExpenses),
            icon: Minus,
            color: 'danger',
            suffix: ' so\'m',
            info: "Mashinalarga sarflangan ehtiyot qismlar narxlari yig'indisi"
        },
        {
            title: 'Nofaol mashinalar',
            value: statistics.inactiveMachines,
            icon: XCircle,
            color: 'warning',
            suffix: 'ta',
            info: "Holati 'inactive' bo'lgan mashinalar"
        }
    ];

    if (statistics.loading) {
        return (
            <div className="card">
                <div className="card-header">
                    <h5 className="card-title mb-0">Mashina statistikasi</h5>
                </div>
                <div className="card-body">
                    <div className="d-flex justify-content-center">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="card">
            <div className="card-header">
                <div className="row align-items-center">
                    <div className="col">
                        <h5 className="card-title mb-0">Mashina statistikasi</h5>
                    </div>
                    <div className="col-auto">
                        <div className="d-flex gap-2">
                            <select 
                                className="form-select"
                                value={selectedMachine}
                                onChange={(e) => setSelectedMachine(e.target.value)}
                                style={{minWidth: '150px'}}
                            >
                                <option value="all">Barcha mashinalar</option>
                                {machines.map(machine => (
                                    <option key={machine.id} value={machine.id}>
                                        {machine.label}
                                    </option>
                                ))}
                            </select>
                            <select 
                                className="form-select"
                                value={selectedMonth}
                                onChange={(e) => setSelectedMonth(e.target.value)}
                                style={{minWidth: '150px'}}
                            >
                                <option value="all">Hammasi</option>
                                {Array.from({ length: 12 }, (_, i) => {
                                    const date = new Date();
                                    date.setMonth(date.getMonth() - i);
                                    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                                    
                                    const uzbekMonths = [
                                        'Yanvar', 'Fevral', 'Mart', 'Aprel', 'May', 'Iyun',
                                        'Iyul', 'Avgust', 'Sentabr', 'Oktabr', 'Noyabr', 'Dekabr'
                                    ];
                                    
                                    const label = `${date.getFullYear()} ${uzbekMonths[date.getMonth()]}`;
                                    
                                    return (
                                        <option key={value} value={value}>
                                            {label}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                    </div>
                </div>
            </div>
            <div className="card-body">
                <div className="row">
                    {cards.map((card, index) => (
                        <div key={index} className="col-12 col-sm-6 col-lg-3 mb-3">
                            <div className={`card border-${card.color} h-100`} style={{position: 'relative'}}>
                                <Popover content={card.info} title={card.title} trigger="click">
                                    <Info size={14} className="text-muted" style={{position: 'absolute', top: 8, right: 8, cursor: 'pointer'}} />
                                </Popover>
                                <div className="card-body">
                                    <div className="d-flex align-items-center">
                                        <div className="flex-shrink-0">
                                            <card.icon className={`feather text-${card.color}`} size={24} />
                                        </div>
                                        <div className="flex-grow-1 ms-3">
                                            <h6 className="card-title mb-0 text-muted">{card.title}</h6>
                                            <h4 className="mt-1 mb-0">
                                                {card.value}{card.suffix}
                                            </h4>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default MachineStatistics;
